import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import CookieBanner, { getCookieConsent } from "./components/CookieBanner";
import TourGuide from "./components/TourGuide";
import { AuthProvider, useAuth } from "./context/AuthContext";
import api from "./api";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import Marketplace from "./pages/Marketplace";
import FindJobs from "./pages/FindJobs";
import BookPage from "./pages/BookPage";
import JobDetail from "./pages/JobDetail";
import TrackPage from "./pages/TrackPage";
import PricingPage from "./pages/PricingPage";
import AccountPage from "./pages/AccountPage";
import CourierDashboard from "./pages/CourierDashboard";
import CompanyDashboard from "./pages/CompanyDashboard";
import ControlTower from "./pages/admin/ControlTower";
import { CmsPage, FaqPage, PublicLoadsPage, PublicLoadDetail } from "./pages/PublicPages";
import { CourierJobsIndex, CourierJobsCountry } from "./pages/SeoJobs";
import { BillingSuccess, BillingCancel } from "./pages/BillingPages";
import VerifyEmail from "./pages/VerifyEmail";
import SetPassword from "./pages/SetPassword";
import ControlTowerLogin from "./pages/ControlTowerLogin";
import { ServicesPage, PlatformPage, AiPage, ForShippersPage, ForCarriersPage } from "./pages/InfoPages";
import { ResourceNewsPage, ResourceRoadAlertsPage, ResourceFuelPage, ResourceFuelCountryPage, ResourceDriverRulesPage } from "./pages/Resources";

function Guard({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return <div className="min-h-[50vh] flex items-center justify-center text-sm text-zinc-400">Loading…</div>;
  }
  if (!user) return <Navigate to={`/auth?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AnalyticsTracker() {
  const loc = useLocation();
  const [consent, setConsent] = React.useState(() => getCookieConsent());
  React.useEffect(() => {
    const onConsent = () => setConsent(getCookieConsent());
    window.addEventListener("cr-consent", onConsent);
    return () => window.removeEventListener("cr-consent", onConsent);
  }, []);
  React.useEffect(() => {
    if (consent !== "all") return; // analytics only with explicit consent (GDPR)
    let vid = localStorage.getItem("cr_vid");
    if (!vid) {
      vid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("cr_vid", vid);
    }
    api.post("/analytics/event", { path: loc.pathname, referrer: document.referrer || null, visitor_id: vid }).catch(() => {});
  }, [loc.pathname, consent]);
  return null;
}

function NoCompanies({ children }) {
  const { user } = useAuth();
  if (user?.role === "company") return <Navigate to="/company" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Layout>
      <AnalyticsTracker />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/control-tower" element={<ControlTowerLogin />} />
        <Route path="/track" element={<TrackPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/platform" element={<PlatformPage />} />
        <Route path="/ai" element={<AiPage />} />
        <Route path="/shippers" element={<ForShippersPage />} />
        <Route path="/carriers" element={<ForCarriersPage />} />
        <Route path="/resources/news" element={<ResourceNewsPage />} />
        <Route path="/resources/road-alerts" element={<ResourceRoadAlertsPage />} />
        <Route path="/resources/fuel-prices" element={<ResourceFuelPage />} />
        <Route path="/resources/fuel-prices/:country" element={<ResourceFuelCountryPage />} />
        <Route path="/resources/driver-rules" element={<ResourceDriverRulesPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/find-jobs" element={<NoCompanies><FindJobs /></NoCompanies>} />
        <Route path="/loads" element={<PublicLoadsPage />} />
        <Route path="/loads/:ref" element={<PublicLoadDetail />} />
        <Route path="/courier-jobs" element={<CourierJobsIndex />} />
        <Route path="/courier-jobs/:country" element={<CourierJobsCountry />} />
        <Route path="/courier-jobs/:country/:city" element={<CourierJobsCountry />} />
        <Route path="/p/:slug" element={<CmsPage />} />
        <Route path="/marketplace" element={<Guard roles={["courier", "admin"]}><Marketplace /></Guard>} />
        <Route path="/book" element={<Guard roles={["company", "admin"]}><BookPage /></Guard>} />
        <Route path="/jobs/:id" element={<Guard><JobDetail /></Guard>} />
        <Route path="/courier" element={<Guard roles={["courier"]}><CourierDashboard /></Guard>} />
        <Route path="/company" element={<Guard roles={["company"]}><CompanyDashboard /></Guard>} />
        <Route path="/control" element={<Guard roles={["admin"]}><ControlTower /></Guard>} />
        <Route path="/account" element={<Guard><AccountPage /></Guard>} />
        <Route path="/billing/success" element={<Guard><BillingSuccess /></Guard>} />
        <Route path="/billing/cancel" element={<BillingCancel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRoutes />
        <CookieBanner />
        <TourGuide />
        <Toaster position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
